'use client';
import React from 'react';
import { getScoreBand } from '@/lib/scoring';
import { Zap, Flame, Activity } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface CategoryCardProps {
  category: 'Engine' | 'Ignition' | 'Bounce';
  score: number;
  label?: string;
  subLabel?: string;
  className?: string;
}

const CATEGORY_META = {
  Engine: { icon: Flame, description: 'Max Strength / Force Production', color: '#8B0000', glow: 'rgba(139,0,0,0.3)' },
  Ignition: { icon: Zap, description: 'Rate of Force Development', color: '#E10600', glow: 'rgba(225,6,0,0.3)' },
  Bounce: { icon: Activity, description: 'Elastic + Reactive Power', color: '#B3B3B3', glow: 'rgba(179,179,179,0.2)' },
};

export default function CategoryCard({ category, score, label, subLabel, className = '' }: CategoryCardProps) {
  const band = getScoreBand(score);
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;

  return (
    <div
      className={`rounded-xl p-4 relative overflow-hidden transition-all duration-200 smoke-overlay ${className}`}
      style={{
        background: 'rgba(20,20,20,0.85)',
        border: `1px solid rgba(255,255,255,0.06)`,
        boxShadow: `0 0 0 0 transparent`,
        cursor: 'default',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.border = `1px solid ${meta.color}30`;
        (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${meta.glow}`;
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.border = `1px solid rgba(255,255,255,0.06)`;
        (e.currentTarget as HTMLElement).style.boxShadow = `0 0 0 0 transparent`;
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Background glow */}
      <div
        className="absolute top-0 right-0 w-24 h-24 rounded-full -translate-y-8 translate-x-8 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${meta.color}15 0%, transparent 70%)` }}
      />
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}25` }}
          >
            <Icon size={16} style={{ color: meta.color }} />
          </div>
          <span
            className="text-xs font-medium px-1.5 py-0.5 rounded-full"
            style={{ background: `${band.color}12`, color: band.color, border: `1px solid ${band.color}20` }}
          >
            {band.label}
          </span>
        </div>
        <div className="mb-1">
          <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">{category}</span>
        </div>
        <div className="flex items-end gap-2 mb-1">
          <span className="font-mono tabular-nums font-black text-3xl" style={{ color: meta.color }}>
            {score}
          </span>
          <span className="text-zinc-600 text-sm mb-1">/100</span>
        </div>
        <p className="text-xs text-zinc-500">{label ?? meta.description}</p>
        {subLabel && <p className="text-xs text-zinc-600 mt-0.5">{subLabel}</p>}
      </div>
    </div>
  );
}